import React from 'react';

export type PublicActionStep = {
  title: string;
  detail?: string;
};

interface PublicActionPageProps {
  eyebrow: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  /** @deprecated Prefer `steps` for richer layout; still mapped to numbered steps */
  features?: string[];
  steps?: PublicActionStep[];
  formTitle: string;
  formDescription: string;
  formEyebrow?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Short line under the steps (trust / privacy / time) */
  asideNote?: string;
}

const PublicActionPage: React.FC<PublicActionPageProps> = ({
  eyebrow,
  title,
  description,
  icon,
  features = [],
  steps,
  formTitle,
  formDescription,
  formEyebrow = 'Get started',
  children,
  footer,
  asideNote,
}) => {
  const resolvedSteps: PublicActionStep[] =
    steps ??
    features.map((feature) => ({
      title: feature,
    }));

  return (
    <main className="public-page-shell w-full">
      <div className="public-content-rail public-action-page">
        <div className="public-action-split">
          <aside
            className="public-action-aside"
            aria-labelledby="public-action-title"
          >
            <div className="public-action-aside__mark" aria-hidden="true">
              {icon}
            </div>
            <p className="public-eyebrow">{eyebrow}</p>
            <h1
              id="public-action-title"
              className="jo-heading public-action-title"
            >
              {title}
            </h1>
            <p className="public-action-lead">{description}</p>

            {resolvedSteps.length > 0 && (
              <ol className="public-action-steps">
                {resolvedSteps.map((step, index) => (
                  <li
                    key={`${step.title}-${index}`}
                    className="public-action-step"
                  >
                    <span
                      className="public-action-step__index"
                      aria-hidden="true"
                    >
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div className="public-action-step__body">
                      <p className="public-action-step__title">{step.title}</p>
                      {step.detail ? (
                        <p className="public-action-step__detail">
                          {step.detail}
                        </p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            )}

            {asideNote ? (
              <p className="public-action-aside__note">{asideNote}</p>
            ) : null}
          </aside>

          <section
            className="public-action-form"
            aria-labelledby="public-action-form-title"
          >
            <header className="public-action-form__header">
              <p className="public-eyebrow">{formEyebrow}</p>
              <h2
                id="public-action-form-title"
                className="jo-heading public-action-form__title"
              >
                {formTitle}
              </h2>
              <p className="public-action-form__desc">{formDescription}</p>
            </header>
            <div className="public-action-form__body">{children}</div>
          </section>
        </div>

        {footer ? <div className="public-action-footer">{footer}</div> : null}
      </div>
    </main>
  );
};

export default PublicActionPage;
